<script setup>
import InputError from "@/Components/InputError.vue";
import QuillEditor from "@/Components/QuillEditor.vue";

import "vue-multiselect/dist/vue-multiselect.min.css";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import Multiselect from "vue-multiselect";
import { defineProps, ref, computed, watch } from "vue";
import Swal from "sweetalert2";
import { useForm } from "@inertiajs/vue3";
import { all } from "axios";

// Ganti ke quill-resize-image
import QuillResizeImage from "quill-resize-image";

import Quill from "quill";
// Register quill-resize-image module
Quill.register("modules/resizeImage", QuillResizeImage);

// Custom image handler untuk auto resize
const imageHandler = function () {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();

  input.onchange = () => {
    const file = input.files[0];
    if (file) {
      // Create image element to get dimensions and resize
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        // Set canvas dimensions to target size
        canvas.width = 800;
        canvas.height = 600;

        // Draw and resize image on canvas
        ctx.drawImage(img, 0, 0, 800, 600);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            // Upload the resized image
            uploadResizedImage(resizedFile);
          },
          file.type,
          0.9
        ); // 0.9 quality
      };

      // Read the original image
      img.src = URL.createObjectURL(file);
    }
  };
};

// Function to upload resized image
const uploadResizedImage = async (file) => {
  const formData = new FormData();
  formData.append("image", file);

  // Show loading placeholder
  const quill = document.querySelector(".custom-quill-editor .ql-editor");
  if (quill) {
    const loadingDiv = document.createElement("div");
    loadingDiv.className = "image-uploading";
    loadingDiv.setAttribute("data-uploading", "true");

    const range = window.getSelection().getRangeAt(0);
    if (range) {
      range.insertNode(loadingDiv);
    }

    try {
      const response = await axios.post(
        route("berita.upload-image"),
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.url) {
        // Remove loading placeholder
        loadingDiv.remove();

        // Create img element with fixed size
        const img = document.createElement("img");
        img.src = response.data.url;
        img.style.width = "800px";
        img.style.height = "600px";
        img.style.objectFit = "cover";
        img.style.display = "block";
        img.style.margin = "10px auto";

        // Insert into editor at the same position
        if (range) {
          range.insertNode(img);
          range.setStartAfter(img);
          range.collapse(true);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
        }

        Swal.fire({
          title: "Berhasil!",
          text: "Gambar berhasil diupload dan diresize ke 800x600px.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      // Remove loading placeholder on error
      loadingDiv.remove();

      console.error("Error uploading image:", error);
      Swal.fire({
        title: "Error!",
        text: "Gagal mengupload gambar.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }
};

// Pastikan modules untuk QuillEditor menggunakan resizeImage
const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
    handlers: {
      image: imageHandler,
    },
  },
  resizeImage: {
    displaySize: true,
    modules: ["Resize", "DisplaySize", "Toolbar"],
    handleStyles: {
      backgroundColor: "#007cff",
      border: "2px solid #fff",
      borderRadius: "50%",
      width: "12px",
      height: "12px",
    },
    defaultSize: {
      width: 800,
      height: 600,
    },
  },
};

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  berita: {
    type: Object,
    required: true,
  },
  allTags: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["update:isOpen"]);

const form = useForm({
  id: null,
  idnews: null,
  title: "",
  content: "",
  publish_date: "",
  published: "",
  tags: [],
});

const normalizedTags = computed(() =>
  Array.isArray(props.allTags)
    ? props.allTags.map((tag) =>
        typeof tag === "string" ? { name: tag } : tag
      )
    : []
);

const availableTags = computed(() => {
  // Gabungkan normalizedTags dan tag baru dari form.tags
  const allTagsArr = normalizedTags.value;
  const tagNames = allTagsArr.map((tag) => tag && tag.name);
  const extraTags = (Array.isArray(form.tags) ? form.tags : []).filter(
    (tag) => tag && tag.name && !tagNames.includes(tag.name)
  );
  // Gabungkan dan hilangkan duplikat berdasarkan name
  const combined = [...allTagsArr, ...extraTags];
  const unique = [];
  const seen = new Set();
  for (const tag of combined) {
    if (tag && tag.name && !seen.has(tag.name)) {
      unique.push(tag);
      seen.add(tag.name);
    }
  }
  return unique;
});
function parseCustomDate(dateStr) {
  if (!dateStr) return "";
  const months = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
  };
  const parts = dateStr.split(" ");
  if (parts.length !== 3) return "";
  const day = parts[0].padStart(2, "0");
  const month = months[parts[1]];
  const year = parts[2].length === 2 ? "20" + parts[2] : parts[2];
  if (!month) return "";
  return `${year}-${month}-${day}`;
}
watch(
  () => props.berita,
  (newBerita) => {
    if (newBerita) {
      form.id = newBerita.id;
      form.idnews = newBerita.idnews;
      form.title = newBerita.title;
      form.content = newBerita.content;
      form.publish_date = newBerita.publish_date
        ? parseCustomDate(newBerita.publish_date)
        : "";
      form.published = newBerita.published;
      form.tags = newBerita.tags.map((name) => ({ name })) || [];
    }
  },
  { immediate: true, deep: true }
);

const closeModal = () => {
  emit("update:isOpen", false);
};
function addTag(newTag) {
  const exists =
    form.tags.some((tag) => tag.name === newTag) ||
    props.allTags.some((tag) => tag.name === newTag);

  if (!exists) {
    form.tags.push({ name: newTag });
  }
}
const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

const submit = async () => {
  // Reset errors
  form.errors = {};

  // Validasi frontend sebelum kirim
  let hasErrors = false;
  const errors = {};

  if (!form.title.trim()) {
    errors.title = "Judul harus diisi.";
    hasErrors = true;
  }

  if (!form.content.trim()) {
    errors.content = "Content harus diisi.";
    hasErrors = true;
  }

  if (!form.publish_date) {
    errors.publish_date = "Tanggal publikasi harus diisi.";
    hasErrors = true;
  }

  if (!form.published || form.published === "") {
    errors.published = "Status publikasi harus diisi.";
    hasErrors = true;
  }

  if (hasErrors) {
    form.errors = errors;
    Swal.fire({
      title: "Validasi Error",
      text: "Mohon lengkapi semua field yang diperlukan",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  // Pastikan form.tags berisi array nama tag
  const selectedTags = form.tags.map((tag) => tag.name);
  const formData = new FormData();
  formData.append("title", form.title);
  formData.append("content", form.content);
  formData.append("publish_date", form.publish_date);
  formData.append("published", form.published);
  formData.append("idnews", form.idnews);
  // Kirim tags sebagai array string
  if (Array.isArray(selectedTags)) {
    selectedTags.forEach((tag) => {
      formData.append("tags[]", tag);
    });
  }
  // Laravel expects _method=PUT for update via POST
  formData.append("_method", "PUT");
  // Reset errors
  form.errors = {};
  try {
    await axios.post(route("berita.update", form.idnews), formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    Swal.fire({
      title: "Berhasil!",
      text: "Berita berhasil disimpan.",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      window.location.reload();
      form.reset();
      form.tags = [];
      form.title = "";
      form.content = "";
      form.publish_date = "";
      form.published = "";
      closeModal();
    });
  } catch (error) {
    if (error.response && error.response.status === 422) {
      form.errors = error.response.data.errors;
      Swal.fire({
        title: "Gagal!",
        text: "Ada kesalahan validasi.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } else {
      Swal.fire({
        title: "Gagal!",
        text: "Ada kesalahan saat menyimpan berita.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }
};
</script>
<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-start justify-center h-screen pt-10 bg-black bg-opacity-50 sm:items-center sm:pt-0"
    style="--modal-max-width: 700px"
    @click="handleClickOutside"
  >
    <div
      class="relative w-full max-w-4xl lg:max-w-5xl p-8 sm:p-10 mx-auto bg-white shadow-2xl rounded-xl overflow-y-auto max-h-[90vh]"
    >
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold">{{ title }}</h2>
        <button
          type="button"
          data-bs-dismiss="modal"
          aria-label="Close"
          @click="closeModal"
          class="text-gray-500 transition duration-150 hover:text-gray-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <form class="space-y-4" @submit.prevent="submit">
        <div>
          <label for="title" class="block text-sm font-medium text-gray-700"
            >Judul</label
          >
          <input
            v-model="form.title"
            type="text"
            id="title"
            name="title"
            class="block w-full p-2 mt-1 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
          />
          <InputError :message="form.errors.title" class="mt-2" />
        </div>

        <div>
          <div class="quill-editor-wrapper">
            <QuillEditor
              v-model="form.content"
              :error="form.errors.content"
              :modules="modules"
              class="custom-quill-editor"
            />
          </div>
          <InputError :message="form.errors.content" class="mt-2" />
        </div>

        <div>
          <label
            for="publish_date"
            class="block text-sm font-medium text-gray-700"
            >Tanggal Publish</label
          >
          <input
            v-model="form.publish_date"
            type="date"
            id="publish_date"
            name="publish_date"
            class="block w-full p-2 mt-1 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
          />
          <InputError :message="form.errors.publish_date" class="mt-2" />
        </div>

        <div>
          <label for="category" class="block text-sm font-medium text-gray-700"
            >Kategori</label
          >
          <Multiselect
            v-model="form.tags"
            :options="availableTags"
            :multiple="true"
            :taggable="true"
            tag-placeholder="Tambah tag"
            placeholder="Pilih atau tambah tag"
            label="name"
            track-by="name"
            @tag="addTag"
            class="w-full"
          />
        </div>
        <div>
          <label for="published" class="block text-sm font-medium text-gray-700"
            >Status Publish</label
          >
          <select
            v-model="form.published"
            id="published"
            class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
            :class="{
              'border-red-500 focus:ring-red-500 focus:border-red-500':
                form.errors.published,
            }"
          >
            <option value="">Pilih Status</option>
            <option value="0">Draft</option>
            <option value="1">Publish</option>
          </select>
          <InputError :message="form.errors.published" class="mt-2" />
        </div>

        <div class="pt-4">
          <button
            type="submit"
            class="w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Simpan Berita
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.quill-editor-wrapper {
  max-width: 100%;
  margin: 0 auto;
}
.custom-quill-editor {
  min-height: 350px;
  max-width: 100%;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 16px;
}
.custom-quill-editor .ql-container {
  min-height: 250px;
  font-size: 1.1rem;
}
.custom-quill-editor .ql-toolbar {
  border-radius: 12px 12px 0 0;
}
.custom-quill-editor .ql-editor img {
  max-width: 100%;
  height: auto;
  cursor: pointer;
  display: inline-block;
  position: relative;
}

/* Image resize handles styling */
.custom-quill-editor .ql-editor .ql-image-resize {
  position: relative;
  display: inline-block;
}

.custom-quill-editor .ql-editor .ql-image-resize img {
  max-width: 100%;
  height: auto;
  cursor: pointer;
}

/* Resize handles */
.custom-quill-editor .ql-editor .ql-image-resize .ql-resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #007cff;
  border: 2px solid #fff;
  border-radius: 50%;
  cursor: nw-resize;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-nw {
  top: -6px;
  left: -6px;
  cursor: nw-resize;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-ne {
  top: -6px;
  right: -6px;
  cursor: ne-resize;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-sw {
  bottom: -6px;
  left: -6px;
  cursor: sw-resize;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-se {
  bottom: -6px;
  right: -6px;
  cursor: se-resize;
}

/* Image toolbar */
.custom-quill-editor .ql-editor .ql-image-toolbar {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  border-radius: 4px;
  padding: 4px 8px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.custom-quill-editor .ql-editor .ql-image-toolbar button {
  background: none;
  border: none;
  color: #fff;
  font-size: 12px;
  padding: 2px 4px;
  margin: 0 2px;
  cursor: pointer;
  border-radius: 2px;
}

.custom-quill-editor .ql-editor .ql-image-toolbar button:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Image alignment classes */
.custom-quill-editor .ql-editor img.ql-align-left {
  float: left;
  margin: 0 16px 16px 0;
}

.custom-quill-editor .ql-editor img.ql-align-right {
  float: right;
  margin: 0 0 16px 16px;
}

.custom-quill-editor .ql-editor img.ql-align-center {
  display: block;
  margin: 16px auto;
}

/* Display size tooltip */
.custom-quill-editor .ql-editor .ql-image-display-size {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 10;
}

/* Auto-resized images styling */
.custom-quill-editor .ql-editor img[style*="width: 800px"] {
  max-width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive behavior for auto-resized images */
@media (max-width: 768px) {
  .custom-quill-editor .ql-editor img[style*="width: 800px"] {
    width: 100% !important;
    height: auto !important;
  }
}

/* Loading state for image upload */
.custom-quill-editor .ql-editor .image-uploading {
  display: inline-block;
  width: 800px;
  height: 600px;
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  position: relative;
}

.custom-quill-editor .ql-editor .image-uploading::after {
  content: "Mengupload gambar...";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #6b7280;
  font-size: 14px;
}
</style>
